import { Box } from '@deckee/deck-hand';
import React, { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  getWeatherPrecisIcon,
  ReactSvg,
} from '../../../../shared/vector-images';
import {
  getTemperatureHeatColorForStops,
  TEMPERATURE_HEAT_COLOUR_STOPS_C,
  TEMPERATURE_HEAT_COLOUR_STOPS_F,
} from '../../helpers/graph/temperatureHeatGradient';
import {
  type WeatherForecastDaySelectHandler,
  type WeatherForecastDaySelectionSource,
} from '../../helpers/analytics/weatherForecastAnalytics';

const DayRow = styled(Box)<{ $isSelected?: boolean; $clickable?: boolean }>`
  border: 1px solid
    ${({ theme, $isSelected }) =>
      $isSelected ? theme.colors.primary : theme.colors.lightergrey};
  border-radius: 8px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const TempBar = styled.div`
  height: 6px;
  border-radius: 3px;
  flex-shrink: 0;
`;

export interface OutlookDay {
  date?: string;
  day: string;
  isWeekend?: boolean;
  weatherIcon?: string;
  minimumTemperature?: number;
  maximumTemperature?: number;
}

export const getOutlookTemperatureScale = (
  forecastDays: OutlookDay[],
): { min: number; max: number } => {
  const definedMinimums = forecastDays
    .map((day) => day.minimumTemperature)
    .filter((value): value is number => value != null);
  const definedMaximums = forecastDays
    .map((day) => day.maximumTemperature)
    .filter((value): value is number => value != null);

  return {
    min: definedMinimums.length ? Math.min(...definedMinimums) : 0,
    max: definedMaximums.length ? Math.max(...definedMaximums) : 0,
  };
};

/** Inclusive degree cells: 29–29 fills the 29° slot; 29–31 fills 29, 30 and 31. */
export const getOutlookTemperatureBarLayout = ({
  minimumTemperature,
  maximumTemperature,
  scaleMin,
  scaleMax,
}: {
  minimumTemperature: number;
  maximumTemperature: number;
  scaleMin: number;
  scaleMax: number;
}): { leftPercent: number; widthPercent: number } => {
  const cellCount = scaleMax - scaleMin + 1;

  if (cellCount <= 0) {
    return { leftPercent: 0, widthPercent: 0 };
  }

  return {
    leftPercent: ((minimumTemperature - scaleMin) / cellCount) * 100,
    widthPercent:
      ((maximumTemperature - minimumTemperature + 1) / cellCount) * 100,
  };
};

export interface OutlookProps {
  forecastDays?: OutlookDay[];
  isFahrenheit?: boolean;
  selectedDate?: string;
  onSelectDay?: WeatherForecastDaySelectHandler;
  daySelectionSource?: WeatherForecastDaySelectionSource;
  /** Inclusive scale shared with sibling outlook sections. */
  scaleMin?: number;
  scaleMax?: number;
}

const Outlook: FC<OutlookProps> = ({
  forecastDays = [],
  isFahrenheit = false,
  selectedDate,
  onSelectDay,
  daySelectionSource = '7 day outlook',
  scaleMin,
  scaleMax,
}) => {
  const colourStops = isFahrenheit
    ? TEMPERATURE_HEAT_COLOUR_STOPS_F
    : TEMPERATURE_HEAT_COLOUR_STOPS_C;

  const isDaySelectable = Boolean(onSelectDay);

  const localScale = useMemo(
    () => getOutlookTemperatureScale(forecastDays),
    [forecastDays],
  );
  const globalMax = scaleMax ?? localScale.max;
  const globalMin = scaleMin ?? localScale.min;

  const handleSelectDay = useCallback(
    (date: string | undefined, dayLabel: string) => {
      if (!date || !onSelectDay) {
        return;
      }

      onSelectDay(date, dayLabel, daySelectionSource);
    },
    [daySelectionSource, onSelectDay],
  );

  if (!forecastDays.length) return null;

  const renderDayRow = (forecastDay: OutlookDay, index: number) => {
    const minimumTemperature = forecastDay.minimumTemperature;
    const maximumTemperature = forecastDay.maximumTemperature;
    const hasMinimum = minimumTemperature != null;
    const hasMaximum = maximumTemperature != null;
    const hasTemperatureRange = hasMinimum && hasMaximum;
    const isSelected = Boolean(
      forecastDay.date && selectedDate && forecastDay.date === selectedDate,
    );
    const isClickable = isDaySelectable && Boolean(forecastDay.date);

    const barLayout =
      hasTemperatureRange &&
      minimumTemperature != null &&
      maximumTemperature != null
        ? getOutlookTemperatureBarLayout({
            minimumTemperature,
            maximumTemperature,
            scaleMin: globalMin,
            scaleMax: globalMax,
          })
        : undefined;

    const leftColour = hasMinimum
      ? getTemperatureHeatColorForStops(minimumTemperature, colourStops)
      : undefined;
    const rightColour = hasMaximum
      ? getTemperatureHeatColorForStops(maximumTemperature, colourStops)
      : undefined;

    const handleClick = () => {
      handleSelectDay(forecastDay.date, forecastDay.day);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleClick();
      }
    };

    return (
      <DayRow
        key={forecastDay.date ?? `${forecastDay.day}-${index}`}
        display="flex"
        alignItems="center"
        px={3}
        py={2}
        mb={2}
        bg={forecastDay.isWeekend ? 'grays.0' : undefined}
        $isSelected={isSelected}
        $clickable={isClickable}
        onClick={isClickable ? handleClick : undefined}
        onKeyDown={isClickable ? handleKeyDown : undefined}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        data-testid={
          forecastDay.date
            ? `weather-forecast-outlook-day-${forecastDay.date}`
            : undefined
        }
        data-selected={isSelected ? 'true' : 'false'}
      >
        <Box
          flex="0 0 7ex"
          mr={2}
          textAlign="right"
          fontSize={2}
          fontWeight={600}
        >
          {forecastDay.day}
        </Box>

        <Box flex="0 0 36px" mr={2}>
          <ReactSvg
            toDisplay={getWeatherPrecisIcon(forecastDay.weatherIcon)}
            width={36}
            height={36}
          />
        </Box>

        <Box flex="1 1 auto" display="flex" alignItems="center">
          <Box
            fontSize="17px"
            color="darkergrey"
            fontWeight={500}
            flex="0 0 auto"
            mr={2}
          >
            {hasMinimum ? `${minimumTemperature}°` : '—'}
          </Box>
          <Box flex="1 1 auto" position="relative" height="6px">
            {barLayout && leftColour && rightColour ? (
              <TempBar
                style={{
                  position: 'absolute',
                  left: `${barLayout.leftPercent}%`,
                  width: `${barLayout.widthPercent}%`,
                  background: `linear-gradient(to right, ${leftColour}, ${rightColour})`,
                }}
              />
            ) : null}
          </Box>
          <Box fontSize="17px" fontWeight={600} flex="0 0 auto" ml={2}>
            {hasMaximum ? `${maximumTemperature}°` : '—'}
          </Box>
        </Box>
      </DayRow>
    );
  };

  return <Box>{forecastDays.map(renderDayRow)}</Box>;
};

export default Outlook;
